import React, { createContext, useContext } from 'react';
import CustomTooltip from '../Tooltip/CustomTooltip';
import type { BombiixIconProps } from '../../bombiix-icon/types';

export interface FloatyProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Placement of the bar:
   * - `inline` (default) — laid out in normal flow wherever it's rendered.
   * - `fixed` — fixed, horizontally centred, 24px from the bottom of the viewport.
   * - `fixed-raised` — same, lifted to 74px (50px approval-bar height + 24px) so
   *   it clears an open approve/reject bar.
   */
  position?: 'inline' | 'fixed' | 'fixed-raised';
}

const POSITION_CLASS = {
  inline: '',
  fixed: 'bombiix-floaty--fixed',
  'fixed-raised': 'bombiix-floaty--fixed bombiix-floaty--above-bar',
} as const;

/**
 * Floaty — a dark floating action toolbar (Figma node 10614:341551). A styled
 * `#3c3c3c` bar (8px radius, dual inset shadow) that lays out whatever children
 * it's given in a flat row. Compose it from `FloatyButton`s for plain actions,
 * or wrap a `FloatyButton` in an `OverlayTrigger` / `CommonDropdown` when a slot
 * needs a popover or dropdown instead of a plain click. `position` fixes it to
 * the bottom-centre of the viewport. Returns `null` when it has no children.
 */
const Floaty: React.FC<FloatyProps> = ({ children, className, position = 'inline' }) => {
  if (!React.Children.toArray(children).length) return null;
  const classes = ['bombiix-floaty', POSITION_CLASS[position], className].filter(Boolean).join(' ');
  return <div className={classes}>{children}</div>;
};

export interface FloatyGroupProps {
  children: React.ReactNode;
  className?: string;
  /**
   * When `true`, every `FloatyButton` in the group is disabled and the whole
   * group reads as one muted `#A4A4A4` well. Reaches the buttons through
   * context, so it applies however deeply they are wrapped — an `OverlayTrigger`
   * or a `Dropdown.Toggle` around a button does not break the cascade.
   */
  disabled?: boolean;
}

/**
 * Carries the group's `disabled` state down to every `FloatyButton` inside it.
 *
 * Context rather than a `cloneElement` cascade: a button is often wrapped in an
 * `OverlayTrigger` or a `Dropdown.Toggle`, and those swallow an injected
 * `disabled` instead of forwarding it to the button they render — so a cascade
 * left the action live while the group beside it greyed out.
 */
const FloatyGroupContext = createContext(false);

/**
 * FloatyGroup — a set of related common actions clubbed into one recessed
 * `#444` "glass" well (Figma node 10614:341498). Renders a single 8px-radius
 * container with one dual-inset glass rim shadow; the child `FloatyButton`s sit
 * flush inside it (square inner corners, only the group's outer corners round)
 * so the run reads as one continuous pill. Pass `disabled` to grey out the
 * whole group at once (the canonical "actions unavailable" state); a `disabled`
 * button inside an otherwise-enabled group still drops to the muted `#A4A4A4`
 * glyph on its own. Use it inside a `Floaty` to separate a cluster of grouped
 * actions from the standalone buttons around it.
 */
const FloatyGroup: React.FC<FloatyGroupProps> = ({ children, className, disabled = false }) => {
  if (!React.Children.toArray(children).length) return null;
  const classes = ['bombiix-floaty-group', !disabled && 'bombiix-floaty-group--flat', className].filter(Boolean).join(' ');
  return (
    <FloatyGroupContext.Provider value={disabled}>
      <div className={classes}>{children}</div>
    </FloatyGroupContext.Provider>
  );
};

export interface FloatyButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  icon: React.ComponentType<BombiixIconProps>;
  /** Accessible name for the icon-only button; also the default tooltip text. */
  label: string;
  variant?: 'blue' | 'red' | 'dark' | 'amber' | 'purple' | 'green' | 'khaki' | 'inactive';
  className?: string;
  /** Hover/focus tooltip text. Defaults to `label`. */
  tooltip?: string;
  /**
   * Whether to render the built-in `CustomTooltip`. Set `false` when this button
   * is the child of an `OverlayTrigger` / dropdown toggle — two overlays fighting
   * over one ref conflict; the bare button is returned instead so the outer
   * trigger can attach.
   */
  showTooltip?: boolean;
  /** Tooltip placement, forwarded to `CustomTooltip`. Defaults to `top`. */
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  /** Icon pixel size. Defaults to 32. */
  size?: number;
}

const ICON_SIZE = 32;

/**
 * A single icon button (32px by default) styled for the Floaty bar
 * (`.bombiix-icon-container` primitive). Forwards its ref and spreads native
 * button props so it can serve directly as an `OverlayTrigger` / react-bootstrap
 * `Dropdown.Toggle` trigger. `variant` maps to a `.bombiix-icon-container--{variant}`
 * modifier; omit for the default white-on-dark glyph. By default the button is
 * wrapped in the shared `CustomTooltip` (hover/focus); pass `showTooltip={false}`
 * when it already sits inside an overlay trigger.
 */
const FloatyButton = React.forwardRef<HTMLButtonElement, FloatyButtonProps>(
  (
    { icon: Icon, label, variant, className, tooltip, showTooltip = true, tooltipPlacement = 'top', type = 'button', size = ICON_SIZE, disabled, ...rest },
    ref,
  ) => {
    const isGroupDisabled = useContext(FloatyGroupContext);
    const isDisabled = disabled || isGroupDisabled;
    const button = (
      <button
        // eslint-disable-next-line react/button-has-type
        type={type}
        ref={ref}
        className={`bombiix-icon-container${variant ? ` bombiix-icon-container--${variant}` : ''}${className ? ` ${className}` : ''}`}
        aria-label={label}
        disabled={isDisabled}
        {...rest}
      >
        <Icon size={size} />
      </button>
    );

    if (!showTooltip) return button;

    return (
      <CustomTooltip placement={tooltipPlacement} tooltipText={tooltip ?? label}>
        {button}
      </CustomTooltip>
    );
  },
);

FloatyButton.displayName = 'FloatyButton';

export { FloatyButton, FloatyGroup };
export default Floaty;
